import { SearchFilters, SingleNews } from '@api/api/news';
import { Formats, formatDateToLocale } from '@helpers/utils/dates/dates';
import { ActiveFilterOptions } from '@terminal/components/filters/Filters';
import { useLanguageStore } from '@terminal/stores/use-language-store/useLanguageStore';
import { getUniqueArray } from '@terminal/utils/common/utils';
import { ASSET_FILTERS_KEY } from '@terminal/views/screener/use-screener-store/filter-utils';
import {
  endOfDay,
  endOfToday,
  endOfYesterday,
  startOfToday,
  startOfYesterday,
} from 'date-fns';

export const TEMP_MIN_FETCHING_NEWS_DATE = endOfDay(new Date('2022-06-30'));
export const QUERY_KEY_MOST_READ_NEWS = 'QUERY_KEY_MOST_READ_NEWS';
export const REFETCH_INTERVAL_DASHBOARD = 10_000;
export const REFETCH_INTERVAL_NEWS_PAGE = 10_000;

export const formatPillDate = (timestamp: Date) => {
  const languageSetting = useLanguageStore.getState().languageSetting;
  return formatDateToLocale(languageSetting, timestamp, Formats['yyyy-MM-dd']);
};

export const formatDateRangeLabel = (date: Date) => {
  const languageSetting = useLanguageStore.getState().languageSetting;
  return formatDateToLocale(languageSetting, date, Formats['MMM dd, yyyy']);
};

export const buildDateRange = (start?: string, end?: string) => {
  if (!start && !end) {
    return undefined;
  }

  return {
    ...(start && { from: start }),
    ...(end && { to: end }),
  };
};

export const formatDateRange = (dateRange: [Date, Date]) => {
  const [formattedFrom, formattedTo] = dateRange.map(formatDateRangeLabel);
  return `${formattedFrom} - ${formattedTo}`;
};

export const groupByDate = <T>(
  items: T[],
  getItemDate: (item: T) => Date
): Record<string, T[]> => {
  return items.reduce<Record<string, T[]>>((acc, item) => {
    const dateKey = formatPillDate(getItemDate(item));

    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }

    acc[dateKey].push(item);
    return acc;
  }, {});
};

export const getUniqueNews = (paginatedNews?: Array<SingleNews[]>) => {
  const newsMap = new Map<string, SingleNews>();

  paginatedNews?.flat().forEach(singleNews => {
    if (!newsMap.has(singleNews.id)) {
      newsMap.set(singleNews.id, singleNews);
    }
  });

  return Array.from(newsMap.values());
};

export const getFiltersFromActiveOptions = (
  activeOptions: ActiveFilterOptions[]
): SearchFilters => {
  const filters: SearchFilters = {};

  activeOptions.forEach(activeOption => {
    if (activeOption.filter === 'market') {
      filters.markets = activeOption.options.map(
        option => option.value.id
      ) as string[];
    }

    if (activeOption.filter === 'providers') {
      filters.providers = activeOption.options.map(
        option => option.value.providers
      ) as string[];
    }

    if (activeOption.filter === ASSET_FILTERS_KEY) {
      const tags = activeOption.options.flatMap(
        option => option.value.tags
      ) as string[];
      filters.entities = getUniqueArray(tags);
    }

    if (activeOption.filter === 'date-picker') {
      const [selectedDateRange] = activeOption.options;

      switch (selectedDateRange.key) {
        case 'today':
          filters.date_range = {
            from: startOfToday().toISOString(),
            to: endOfToday().toISOString(),
          };
          break;
        case 'yesterday':
          filters.date_range = {
            from: startOfYesterday().toISOString(),
            to: endOfYesterday().toISOString(),
          };
          break;

        default:
          filters.date_range = {
            ...selectedDateRange.value,
          };
          break;
      }
    }
  });

  return filters;
};
